import { useContext, useMemo, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/ru';
import { TranslationContext } from '../../../assets/contexts/translationContext';
import { CrossIcon } from '../../../assets/icons/icons';
import { triggerImpactHaptic } from '../../../assets/utils/hapticFeedback';
import HistoryCardSheet from './HistoryCardSheet';
import HistoryPeriodSheet from './HistoryPeriodSheet';
import './HistoryFilters.css';

function capitalize(value) {
  return value ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : '';
}

function getDateLocale(language) {
  return String(language || '').toLowerCase().startsWith('ru') ? 'ru' : 'en';
}

export function formatHistoryPeriodLabel(dateRange, language, now = dayjs()) {
  if (!dateRange?.start || !dateRange?.end) {
    return '';
  }

  const locale = getDateLocale(language);
  const start = dayjs(dateRange.start).locale(locale);
  const end = dayjs(dateRange.end).locale(locale);

  if (!start.isValid() || !end.isValid()) {
    return '';
  }

  if (dateRange.mode === 'month') {
    return capitalize(start.format(start.isSame(dayjs(now), 'year') ? 'MMMM' : 'MMMM YYYY'));
  }

  if (start.isSame(end, 'day')) {
    return start.format('D MMMM');
  }

  if (start.isSame(end, 'month')) {
    return `${start.format('D')}–${end.format('D MMMM')}`;
  }

  if (start.isSame(end, 'year')) {
    return `${start.format('D MMM')}–${end.format('D MMM')}`;
  }

  return `${start.format('D MMM YYYY')}–${end.format('D MMM YYYY')}`;
}

export function getCurrentHistoryMonthRange(now = dayjs()) {
  const currentDate = dayjs(now).startOf('day');

  return getHistoryMonthRange(currentDate.format('YYYY-MM'), currentDate);
}

export function getHistoryMonthRange(monthKey, now = dayjs()) {
  const currentDate = dayjs(now).startOf('day');
  const month = dayjs(`${monthKey}-01`).startOf('month');

  if (
    !month.isValid()
    || month.format('YYYY-MM') !== monthKey
    || month.isAfter(currentDate, 'month')
  ) {
    return null;
  }

  return {
    start: month.format('YYYY-MM-DD'),
    end: (month.isSame(currentDate, 'month') ? currentDate : month.endOf('month'))
      .format('YYYY-MM-DD'),
    mode: 'month',
  };
}

export function formatHistoryCardsLabel(selectedCardIds, cards, history) {
  if (!Array.isArray(selectedCardIds) || selectedCardIds.length === 0) {
    return '';
  }

  if (selectedCardIds.length === 1) {
    const selectedCard = cards.find((card) => card.id === selectedCardIds[0]);

    if (selectedCard) {
      const label = selectedCard.label || selectedCard.name || history.unnamedCard;
      const lastFour = selectedCard.lastFour || selectedCard.last4;

      return lastFour ? `${label} •${lastFour}` : label;
    }
  }

  return String(history.cardsCount || 'Cards {{count}}')
    .replace('{{count}}', selectedCardIds.length);
}

function ChevronDown() {
  return (
    <svg
      className="history-filter-chip__chevron"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="m4.25 6.25 3.75 4 3.75-4"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function FilterChip({
  active,
  activeWithChevron = false,
  expanded,
  label,
  onClear,
  onOpen,
  openAriaLabel,
  clearAriaLabel,
}) {
  const canClear = Boolean(active && !activeWithChevron && onClear);
  const className = [
    'history-filter-chip',
    active ? 'history-filter-chip--active' : '',
    canClear ? 'history-filter-chip--clearable' : '',
  ].filter(Boolean).join(' ');

  return (
    <span className={className}>
      <button
        type="button"
        className="history-filter-chip__main"
        aria-label={openAriaLabel}
        aria-haspopup="dialog"
        aria-expanded={expanded}
        onClick={onOpen}
      >
        <span className="history-filter-chip__label">{label}</span>
        {(!active || activeWithChevron) ? <ChevronDown /> : null}
      </button>
      {canClear ? (
        <button
          type="button"
          className="history-filter-chip__clear"
          aria-label={clearAriaLabel}
          onClick={onClear}
        >
          <CrossIcon className="history-filter-chip__clear-icon" />
        </button>
      ) : null}
    </span>
  );
}

function HistoryFilters({
  cards = [],
  dateRange,
  isDateRangeUserSelected = false,
  minDate,
  selectedCardIds = [],
  onDateRangeChange,
  onSelectedCardIdsChange,
}) {
  const { history, language } = useContext(TranslationContext);
  const [activeSheet, setActiveSheet] = useState(null);
  const periodLabel = useMemo(
    () => formatHistoryPeriodLabel(dateRange, language),
    [dateRange, language]
  );
  const cardsLabel = useMemo(
    () => formatHistoryCardsLabel(selectedCardIds, cards, history),
    [cards, history, selectedCardIds]
  );
  const hasPeriod = Boolean(periodLabel);
  const hasCards = selectedCardIds.length > 0;

  const openSheet = (sheet) => {
    triggerImpactHaptic('light');
    setActiveSheet(sheet);
  };

  const clearPeriod = () => {
    triggerImpactHaptic('light');
    onDateRangeChange(null);
  };

  const clearCards = () => {
    triggerImpactHaptic('light');
    onSelectedCardIdsChange([]);
  };

  return (
    <>
      <div className="history-filters" aria-label={history.filtersAria}>
        <FilterChip
          active={hasPeriod}
          activeWithChevron={dateRange?.mode === 'month'}
          expanded={activeSheet === 'period'}
          label={periodLabel || history.periodFilter}
          openAriaLabel={history.openPeriodFilterAria}
          clearAriaLabel={history.clearPeriodFilterAria}
          onOpen={() => openSheet('period')}
          onClear={clearPeriod}
        />
        <FilterChip
          active={hasCards}
          expanded={activeSheet === 'cards'}
          label={cardsLabel || history.cardsFilter}
          openAriaLabel={history.openCardsFilterAria}
          clearAriaLabel={history.clearCardsFilterAria}
          onOpen={() => openSheet('cards')}
          onClear={clearCards}
        />
      </div>

      <AnimatePresence>
        {activeSheet === 'period' ? (
          <HistoryPeriodSheet
            key="history-period-sheet"
            value={dateRange}
            isUserSelection={isDateRangeUserSelected}
            minDate={minDate}
            onClose={() => setActiveSheet(null)}
            onApply={(nextDateRange) => {
              onDateRangeChange(nextDateRange);
              setActiveSheet(null);
            }}
          />
        ) : null}
        {activeSheet === 'cards' ? (
          <HistoryCardSheet
            key="history-card-sheet"
            cards={cards}
            value={selectedCardIds}
            onClose={() => setActiveSheet(null)}
            onApply={(nextCardIds) => {
              onSelectedCardIdsChange(nextCardIds);
              setActiveSheet(null);
            }}
          />
        ) : null}
      </AnimatePresence>
    </>
  );
}

export default HistoryFilters;
